import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import type {
  MutationClaimOrganizationArgs,
  MutationLinkEnterpriseWithAtlassianOrganizationArgs,
  EnterpriseClaimableOrganizationsArgs,
  MutationStartEnterpriseExportArgs,
  MutationDeleteManagedMemberTokensArgs,
  MutationUpdateEnterpriseApiTokenCreationPermissionArgs,
  EnterpriseManagedMembersWithTokensArgs,
  EnterpriseExportArgs,
  EnterpriseTransferrableDataArgs,
  QuerySelfServeExpansionEstimateArgs,
  MutationCreateSelfServeExpansionArgs,
} from '../generated';
import { token } from '@trello/session-cookie';
import { trelloFetch, fetch } from '@trello/fetch';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { Analytics } from '@trello/atlassian-analytics';

import type { TrelloRestResolver } from '../types';
import { getNetworkClient } from '../getNetworkClient';
import { isQueryInfo } from '../isQueryInfo';
import { featureFlagClient } from '@trello/feature-flag-client';

export const enterpriseClaimableOrganizationsResolver: TrelloRestResolver<EnterpriseClaimableOrganizationsArgs> = async (
  enterprise: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;
  const { limit, cursor, name, activeSince, inactiveSince } = args;

  const nameQuery = name ? `&name=${name}` : '';

  const activeSinceQuery =
    activeSince && !inactiveSince ? `&activeSince=${activeSince}` : '';

  const inactiveSinceQuery =
    !activeSince && inactiveSince ? `&inactiveSince=${inactiveSince}` : '';

  const queryParams = `limit=${limit || 20}&cursor=${encodeURIComponent(
    cursor || '',
  )}${nameQuery}${activeSinceQuery}${inactiveSinceQuery}`;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${enterprise.id}/claimableOrganizations?${queryParams}`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Enterprise.claimableOrganizations',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      const {
        organizations,
        claimableCount,
        cursor: nextCursor,
      } = await response.json();

      model = {
        organizations,
        count: claimableCount,
        cursor: nextCursor,
      };
    } else {
      throw new Error(
        `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
      );
    }

    return model
      ? prepareDataForApolloCache(model, rootNode, 'Enterprise')
      : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const transferrableDataForOrganizationResolver: TrelloRestResolver<EnterpriseTransferrableDataArgs> = async (
  enterprise: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${enterprise.id}/transferrable/bulk?idOrganizations=${args.idOrganizations}`,
  );

  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'Enterprise.transferrableData',
      operationName: context.operationName,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    console.error(error);
    throw new Error(error);
  }

  const model = await response.json();

  // The API should probably not be sending back status 200 for this, but alas
  if ('message' in model) {
    console.error(model);
    throw new Error(model.message);
  }

  return model
    ? prepareDataForApolloCache(model.organizations, rootNode, 'Enterprise')
    : model;
};

export const startEnterpriseExport: TrelloRestResolver<MutationStartEnterpriseExportArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const response = await fetch(`/1/enterprises/${args.id}/exports`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
      enterpriseAssociationType: args.enterpriseAssociationType,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const getEnterpriseExportResolver: TrelloRestResolver<EnterpriseExportArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const response = await trelloFetch(
    `/1/enterprise/${args.idEnterprise}/exports/${args.idExport}`,
    undefined,
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Enterprise.export',
        operationName: context.operationName,
      },
    },
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }
  const body = await response.json();
  return prepareDataForApolloCache(body, rootNode, 'Enterprise');
};

export const claimOrganization: TrelloRestResolver<MutationClaimOrganizationArgs> = async (
  obj,
  { idEnterprise, idOrganizations, traceId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();

  const areBulkActionsEnabled = featureFlagClient.get(
    'enterprise.bulk-add-workspaces',
    false,
  );
  let response;
  if (areBulkActionsEnabled) {
    response = await fetch(
      networkClient.getUrl(`/1/enterprises/${idEnterprise}/organizations/bulk`),
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Trello-Client-Version': context.clientAwareness.version,
          ...(traceId && Analytics.getTaskRequestHeaders(traceId)),
        },
        body: JSON.stringify({
          idOrganizations,
          token: context.token || token,
        }),
      },
    );
  } else {
    // Legacy behavior
    if (idOrganizations.length > 1) {
      throw new Error(`May not supply multiple organizations in this mutation`);
    }

    response = await fetch(
      networkClient.getUrl(`/1/enterprises/${idEnterprise}/organizations`),
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Trello-Client-Version': context.clientAwareness.version,
          ...(traceId && Analytics.getTaskRequestHeaders(traceId)),
        },
        body: JSON.stringify({
          idOrganization: idOrganizations[0],
          token: context.token || token,
        }),
      },
    );
  }

  if (!response.ok) {
    const error = await response.text();
    console.error(error);
    throw new Error(error);
  }

  const model = await response.json();

  return prepareDataForApolloCache(model, rootNode);
};

export const linkEnterpriseWithAtlassianOrganization: TrelloRestResolver<MutationLinkEnterpriseWithAtlassianOrganizationArgs> = async (
  obj,
  { idEnterprise, atlOrgId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${idEnterprise}/linkWithAtlassianOrganization`,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      atlOrgId,
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  await response.json();
  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const managedMembersWithTokensResolver: TrelloRestResolver<EnterpriseManagedMembersWithTokensArgs> = async (
  enterprise: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${enterprise.id}/members/tokens?filter=${args.filter}`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Enterprise.managedMembersWithTokens',
        operationName: context.operationName,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error);
    }

    model = await response.json();

    return model
      ? prepareDataForApolloCache(model, rootNode, 'Enterprise')
      : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const deleteManagedMemberTokens: TrelloRestResolver<MutationDeleteManagedMemberTokensArgs> = async (
  obj,
  { idEnterprise, idMember, filter },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(
      `/1/enterprises/${idEnterprise}/members/tokens/${idMember}`,
    ),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        filter,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.text();
    console.error(error);
    throw new Error(error);
  }

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const deleteAllManagedMemberTokens: TrelloRestResolver<MutationDeleteManagedMemberTokensArgs> = async (
  obj,
  { idEnterprise, filter },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/enterprises/${idEnterprise}/members/tokens`),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        filter,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.text();
    console.error(error);
    throw new Error(error);
  }

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const updateEnterpriseApiTokenCreationPermission: TrelloRestResolver<MutationUpdateEnterpriseApiTokenCreationPermissionArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/enterprise/${args.idEnterprise}`),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        'prefs/canIssueManagedConsentTokens': args.isAllowed,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const auditlogResolver: TrelloRestResolver<null> = async (
  enterprise: { id: string },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await trelloFetch(
    networkClient.getUrl(`/1/enterprise/${enterprise.id}/auditlog`),
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
    },
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode, 'Enterprise');
};

export const selfServeExpansionEstimateResolver: TrelloRestResolver<QuerySelfServeExpansionEstimateArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${args.idEnterprise}/seat-expansion-estimated-price/${args.seats}`,
  );
  const response = await trelloFetch(apiUrl, {
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': context.clientAwareness.version,
      Authorization: `Bearer ${document.cookie}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }

  const result = await response.json();

  return result;
};

export const createSelfServeExpansion: TrelloRestResolver<MutationCreateSelfServeExpansionArgs> = async (
  obj,
  args,
  context,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${args.idEnterprise}/seat-expansion`,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': context.clientAwareness.version,
      Authorization: `Bearer ${document.cookie}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      seats: args.seats,
      token,
    }),
  });

  return { ok: response.ok };
};
